import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Calendar, CheckSquare, Bell, Plus, Edit, Filter } from 'lucide-react';
import StatusChip from '../components/StatusChip';
import DatePicker from '../components/DatePicker';
import EnhancedTaskDialog from '../components/EnhancedTaskDialog';
import { api } from '../utils/api';
import { formatDate, getDaysUntil } from '../utils/helpers';
import { toast } from 'sonner';

const TasksCalendarView = ({ user }) => {
  // Tasks state
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Calendar state
  const [dueItems7, setDueItems7] = useState(null);
  const [dueItems30, setDueItems30] = useState(null);
  const [dueItems60, setDueItems60] = useState(null);
  
  // Enhanced Task Dialog state
  const [enhancedDialogOpen, setEnhancedDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  
  // Legacy Reminder dialog state (keep for backward compatibility)
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderForm, setReminderForm] = useState({
    title: '',
    description: '',
    date: '',
    time: '09:00',
    type: 'task',
    priority: 'medium',
    sendEmail: false,
    emailRecipient: user?.email || ''
  });
  
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [priorityFilter, statusFilter, tasks]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [tasksData, data7, data30, data60] = await Promise.all([
        api.getTasks(),
        api.getDueItems('7'),
        api.getDueItems('30'),
        api.getDueItems('60')
      ]);
      setTasks(tasksData.tasks || []);
      setFilteredTasks(tasksData.tasks || []);
      setDueItems7(data7);
      setDueItems30(data30);
      setDueItems60(data60);
      
      // Automatically create tasks for upcoming renewals
      await autoCreateRenewalTasks(data7, data30, data60, tasksData.tasks || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const autoCreateRenewalTasks = async (data7, data30, data60, existingTasks) => {
    try {
      const allRenewals = [];
      
      // Collect all renewal items from 7, 30, and 60 day buckets
      [data7, data30, data60].forEach((data, index) => {
        if (!data) return;
        const bucket = index === 0 ? '7' : index === 1 ? '30' : '60';
        
        // Add licenses
        (data.licenses || []).forEach(license => {
          allRenewals.push({
            type: 'License',
            itemType: 'license',
            id: license.license_id,
            title: `Renew ${license.license_type} - ${license.state}`,
            description: `License renewal for ${license.license_type} in ${license.state}. Expiration: ${formatDate(license.expiration_date)}`,
            dueDate: license.expiration_date,
            state: license.state,
            bucket: bucket,
            licenseNumber: license.license_number
          });
        });
        
        // Add filings
        (data.filings || []).forEach(filing => {
          allRenewals.push({
            type: 'Filing',
            itemType: 'filing',
            id: filing.filing_id,
            title: `Complete ${filing.filing_type} - ${filing.state}`,
            description: `Filing due for ${filing.filing_type} in ${filing.state}. Due date: ${formatDate(filing.due_date)}`,
            dueDate: filing.due_date,
            state: filing.state,
            bucket: bucket,
            filingNumber: filing.filing_number
          });
        });
        
        // Add DBAs
        (data.dbas || []).forEach(dba => {
          allRenewals.push({
            type: 'DBA',
            itemType: 'dba',
            id: dba.dba_id,
            title: `Renew DBA: ${dba.dba_name} - ${dba.state}`,
            description: `DBA registration renewal for ${dba.dba_name} in ${dba.state}. Expiration: ${formatDate(dba.expiration_date)}`,
            dueDate: dba.expiration_date,
            state: dba.state,
            bucket: bucket,
            registrationNumber: dba.registration_number
          });
        });
      });
      
      // Check which renewals don't have tasks yet and create them
      for (const renewal of allRenewals) {
        // Check if task already exists for this renewal
        const taskExists = existingTasks.some(task => 
          task.title === renewal.title || 
          (task.description && task.description.includes(renewal.id))
        );
        
        if (!taskExists) {
          // Determine priority based on bucket
          const priority = renewal.bucket === '7' ? 'urgent' : 
                          renewal.bucket === '30' ? 'high' : 'medium';
          
          // Create the task
          const taskPayload = {
            title: renewal.title,
            description: renewal.description,
            due_date: renewal.dueDate,
            priority: priority,
            status: 'open',
            assigned_to: user?.email || 'admin',
            category: `${renewal.type} Renewal`,
            related_entity_id: renewal.id,
            related_entity_type: renewal.itemType
          };
          
          try {
            await api.createTask(taskPayload);
            console.log(`Auto-created task for ${renewal.type}: ${renewal.title}`);
          } catch (error) {
            console.error(`Failed to auto-create task for ${renewal.title}:`, error);
            // Continue with other tasks even if one fails
          }
        }
      }
    } catch (error) {
      console.error('Failed to auto-create renewal tasks:', error);
      // Don't throw - this is a background operation
    }
  };

  const applyFilters = () => {
    let filtered = [...tasks];
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(t => t.priority === priorityFilter);
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter);
    }
    setFilteredTasks(filtered);
  };

  const handleSyncCalendarTasks = async () => {
    try {
      toast.info('Syncing calendar items with tasks...');
      await loadAllData();  // This will trigger auto-create
      toast.success('Calendar tasks synced successfully!');
    } catch (error) {
      console.error('Failed to sync calendar tasks:', error);
      toast.error('Failed to sync calendar tasks');
    }
  };

  const handleToggleTask = async (taskId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'open' ? 'completed' : 'open';
      await api.updateTask(taskId, { status: newStatus });
      await loadAllData();
      toast.success('Task updated successfully');
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error('Failed to update task');
    }
  };

  const handleSetReminder = () => {
    setReminderForm({
      title: '',
      description: '',
      date: '',
      time: '09:00',
      type: 'task',
      priority: 'medium',
      sendEmail: false,
      emailRecipient: user?.email || ''
    });
    setReminderDialogOpen(true);
  };

  const handleSaveReminder = async () => {
    try {
      // Start with just the date (most compatible format)
      let dueDate = reminderForm.date;
      
      // If time is provided, try datetime format
      if (reminderForm.time) {
        dueDate = `${reminderForm.date}T${reminderForm.time}:00`;
      }

      const taskData = {
        title: reminderForm.title,
        description: reminderForm.description || '',
        due_date: dueDate,
        priority: reminderForm.priority,
        status: 'open',
        assigned_to: user.email
      };
      
      console.log('Creating task with data:', taskData);
      
      // First attempt with full payload
      try {
        await api.createTask(taskData);
        await loadAllData();
        toast.success('Reminder set successfully');
        setReminderDialogOpen(false);
        return;
      } catch (error) {
        console.error('First attempt failed:', error);
        
        // If error mentions datetime or time, try with just date
        if (error.message?.includes('date') || error.message?.includes('time')) {
          console.log('Retrying with date-only format...');
          taskData.due_date = reminderForm.date;
          
          await api.createTask(taskData);
          await loadAllData();
          toast.success('Reminder set successfully (time not supported)');
          setReminderDialogOpen(false);
          return;
        }
        
        throw error;
      }
    } catch (error) {
      console.error('Failed to create reminder:', error);
      
      // Show user-friendly error
      const errorMessage = error.message || 'Unknown error';
      toast.error(`Failed to set reminder: ${errorMessage}. Please check the console for details.`);
    }
  };

  const handleSaveEnhancedTask = async (taskData) => {
    try {
      // Map frontend fields to backend API format
      const payload = {
        // Core fields
        title: taskData.title || 'Untitled Task',
        description: taskData.description || '',
        task_type: taskData.taskType || 'renewal',
        state: taskData.state || '',
        compliance_category: taskData.complianceCategory || 'license',
        item_name: taskData.itemName || '',
        
        // Dates - ensure proper datetime format
        due_date: taskData.renewalDueDate || taskData.expirationDate || new Date().toISOString(),
        issue_date: taskData.issueDate || null,
        effective_date: taskData.effectiveDate || null,
        expiration_date: taskData.expirationDate || null,
        renewal_due_date: taskData.renewalDueDate || null,
        
        // Financials
        filing_fee: taskData.filingFee ? parseFloat(taskData.filingFee) : null,
        payment_status: taskData.paymentStatus || 'unpaid',
        payment_date: taskData.paymentDate || null,
        
        // Renewal logic
        renewal_cycle: taskData.renewalCycle || 'annual',
        custom_cycle_days: taskData.customCycleDays ? parseInt(taskData.customCycleDays) : null,
        reminder_thresholds: taskData.reminderThresholds || '60,30,15,7',
        
        // Assignment
        assigned_to: taskData.assignedTo || user?.email || '',
        priority: taskData.priority || 'medium',
        status: taskData.status || 'open',
        
        // Links
        related_entity_id: taskData.relatedEntityId || '',
        related_entity_type: taskData.relatedEntityType || ''
      };
      
      console.log('Creating enhanced task with payload:', payload);
      
      // Create the task
      const response = await api.createTask(payload);
      const taskId = response.task_id;
      
      // Upload document if file was selected
      if (taskData.documentFile && taskId) {
        try {
          console.log('Uploading document for task:', taskId);
          const formData = new FormData();
          formData.append('file', taskData.documentFile);
          formData.append('category', 'task_evidence');
          formData.append('notes', taskData.description || 'Task evidence document');
          
          const backendUrl = process.env.REACT_APP_BACKEND_URL;
          const uploadResponse = await fetch(`${backendUrl}/api/tasks/${taskId}/documents`, {
            method: 'POST',
            body: formData,
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (uploadResponse.ok) {
            console.log('Document uploaded successfully');
            toast.success('Task created with document attached');
          } else {
            console.error('Document upload failed:', await uploadResponse.text());
            toast.warning('Task created but document upload failed');
          }
        } catch (uploadError) {
          console.error('Document upload error:', uploadError);
          toast.warning('Task created but document upload failed');
        }
      } else {
        toast.success('Compliance task created successfully');
      }
      
      await loadAllData();
      setEnhancedDialogOpen(false);
      setSelectedTask(null);
    } catch (error) {
      console.error('Failed to create enhanced task:', error);
      toast.error(`Failed to create task: ${error.message || 'Unknown error'}`);
    }
  };

  const renderCalendarBucket = (title, data, bucket, variant) => {
    if (!data) return null;

    const allItems = [
      ...(data.licenses || []).map(l => ({ ...l, type: 'License', due: l.expiration_date })),
      ...(data.filings || []).map(f => ({ ...f, type: 'Filing', due: f.due_date })),
      ...(data.dbas || []).map(d => ({ ...d, type: 'DBA', due: d.expiration_date }))
    ].sort((a, b) => new Date(a.due) - new Date(b.due));

    // Map bucket to color scheme based on design guidelines
    const getBucketColor = (bucketDays) => {
      const days = parseInt(bucketDays);
      if (days <= 7) {
        return {
          bg: 'bg-rose-50',
          text: 'text-rose-950',
          border: 'border-rose-200',
          badge: 'bg-rose-500'
        };
      } else if (days <= 30) {
        return {
          bg: 'bg-amber-50',
          text: 'text-amber-950',
          border: 'border-amber-200',
          badge: 'bg-amber-500'
        };
      } else {
        return {
          bg: 'bg-yellow-50',
          text: 'text-yellow-900',
          border: 'border-yellow-200',
          badge: 'bg-yellow-500'
        };
      }
    };

    const colorScheme = getBucketColor(bucket);

    return (
      <Card key={bucket} data-testid={`calendar-bucket-${bucket}`} className="flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{title}</CardTitle>
            <Badge variant={variant}>{allItems.length} items</Badge>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          {allItems.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No items due in this period</p>
          ) : (
            <div className="space-y-3">
              {allItems.map((item, idx) => {
                const daysUntil = getDaysUntil(item.due);
                const isOverdue = daysUntil < 0;
                const isDueVerySoon = daysUntil >= 0 && daysUntil <= 7;
                const isDueSoon = daysUntil > 7 && daysUntil <= 30;
                
                // Dynamic color based on countdown
                const countdownColor = isOverdue 
                  ? 'text-rose-600' 
                  : isDueVerySoon 
                    ? 'text-rose-600' 
                    : isDueSoon 
                      ? 'text-amber-600' 
                      : 'text-emerald-600';
                
                return (
                  <div 
                    key={idx} 
                    className={`p-3 rounded-lg border ${isOverdue || isDueVerySoon ? colorScheme.border : 'border-border'} ${isOverdue || isDueVerySoon ? colorScheme.bg : 'hover:bg-muted/50'} transition-colors`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge variant="outline" className="text-xs">{item.type}</Badge>
                          <Badge variant="outline" className="text-xs">{item.state}</Badge>
                        </div>
                        <div className="text-sm font-medium truncate">
                          {item.filing_type || item.license_type || item.dba_name}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Due: {formatDate(item.due)}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className={`text-sm font-medium ${countdownColor}`} data-testid="task-countdown-badge">
                          {isOverdue ? `${Math.abs(daysUntil)} days overdue` : `${daysUntil} days`}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="tasks-calendar-container">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">Tasks & Calendar</h1>
          <p className="text-muted-foreground mt-2">Manage compliance tasks and view upcoming deadlines</p>
        </div>
        <Button onClick={() => setEnhancedDialogOpen(true)} data-testid="task-create-open-button">
          <Plus className="h-4 w-4 mr-2" />
          New Task
        </Button>
      </div>

      <Tabs defaultValue="tasks" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="tasks" data-testid="tasks-tab">
            <CheckSquare className="h-4 w-4 mr-2" />
            Tasks
          </TabsTrigger>
          <TabsTrigger value="calendar" data-testid="calendar-tab">
            <Calendar className="h-4 w-4 mr-2" />
            Calendar
          </TabsTrigger>
        </TabsList>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="space-y-6">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Filter className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Filter by priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>

                <div className="text-sm text-muted-foreground sm:ml-auto">
                  Showing {filteredTasks.length} of {tasks.length} tasks
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tasks List */}
          <div className="space-y-3">
            {filteredTasks.map((task) => {
              // Safety check for null tasks
              if (!task || !task.task_id) {
                console.warn('Skipping null or invalid task');
                return null;
              }
              
              // Calculate countdown styling based on due_bucket from backend
              const getCountdownStyle = () => {
                if (!task.due_bucket) {
                  // Fallback if backend doesn't provide due_bucket yet
                  if (!task.due_date) {
                    return { variant: 'secondary', text: 'No due date', color: 'text-muted-foreground' };
                  }
                  const daysUntil = getDaysUntil(task.due_date);
                  if (daysUntil < 0) return { variant: 'destructive', text: `${Math.abs(daysUntil)}d overdue`, color: 'text-rose-600' };
                  if (daysUntil <= 7) return { variant: 'destructive', text: `${daysUntil}d`, color: 'text-rose-600' };
                  if (daysUntil <= 30) return { variant: 'secondary', text: `${daysUntil}d`, color: 'text-amber-600' };
                  if (daysUntil <= 60) return { variant: 'secondary', text: `${daysUntil}d`, color: 'text-yellow-700' };
                  return { variant: 'secondary', text: `${daysUntil}d`, color: 'text-emerald-600' };
                }

                // Use backend due_bucket
                const bucket = task.due_bucket;
                const days = task.due_in_days || 0;
                
                if (bucket === 'overdue') {
                  return { variant: 'destructive', text: `${Math.abs(days)}d overdue`, color: 'text-rose-950' };
                } else if (bucket === '0-7') {
                  return { variant: 'destructive', text: `${days}d`, color: 'text-rose-950' };
                } else if (bucket === '8-30') {
                  return { variant: 'secondary', text: `${days}d`, color: 'text-amber-950' };
                } else if (bucket === '31-60') {
                  return { variant: 'secondary', text: `${days}d`, color: 'text-yellow-900' };
                } else {
                  return { variant: 'secondary', text: `${days}d`, color: 'text-emerald-700' };
                }
              };

              const countdown = getCountdownStyle();

              return (
                <Card key={task.task_id} data-testid={`task-card-${task.task_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Checkbox
                        checked={task.status === 'completed'}
                        onCheckedChange={() => handleToggleTask(task.task_id, task.status)}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                            {task.title}
                          </h3>
                          <Badge variant={task.priority === 'urgent' ? 'destructive' : 'secondary'} className="text-xs">
                            {task.priority}
                          </Badge>
                          {task.due_date && task.status !== 'completed' && (
                            <Badge variant={countdown.variant} className="text-xs" data-testid="task-countdown-badge">
                              {countdown.text}
                            </Badge>
                          )}
                          {task.state && (
                            <Badge variant="outline" className="text-xs">
                              {task.state}
                            </Badge>
                          )}
                        </div>
                        {task.description && (
                          <p className="text-sm text-muted-foreground mb-2">{task.description}</p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                          {task.due_date && (
                            <span>Due: {formatDate(task.due_date)}</span>
                          )}
                          {task.assigned_to && (
                            <span>Assigned: {task.assigned_to}</span>
                          )}
                          {task.filing_fee && (
                            <span className="font-medium tabular-nums">Cost: ${parseFloat(task.filing_fee).toFixed(2)}</span>
                          )}
                          {task.compliance_category && (
                            <span className="capitalize">{task.compliance_category.replace(/-/g, ' ')}</span>
                          )}
                        </div>
                      </div>
                      <StatusChip status={task.status} />
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {filteredTasks.length === 0 && (
              <Card>
                <CardContent className="p-12 text-center text-muted-foreground" data-testid="tasks-empty-state">
                  No tasks match the current filters
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="space-y-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              Upcoming renewals and deadlines. Tasks are automatically created for each item.
            </p>
            <Button onClick={handleSyncCalendarTasks} variant="outline" size="sm">
              <CheckSquare className="h-4 w-4 mr-2" />
              Sync to Tasks
            </Button>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {renderCalendarBucket('Next 7 Days', dueItems7, '7', 'destructive')}
            {renderCalendarBucket('Next 30 Days', dueItems30, '30', 'default')}
            {renderCalendarBucket('Next 60 Days', dueItems60, '60', 'secondary')}
          </div>
        </TabsContent>
      </Tabs>

      {/* Set Reminder Dialog */}
      <Dialog open={reminderDialogOpen} onOpenChange={setReminderDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Reminder</DialogTitle>
            <DialogDescription>Create a new task or reminder for a future deadline</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reminder-title">Title *</Label>
              <Input
                id="reminder-title"
                value={reminderForm.title}
                onChange={(e) => setReminderForm({ ...reminderForm, title: e.target.value })}
                placeholder="Enter reminder title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="reminder-description">Description</Label>
              <Input
                id="reminder-description"
                value={reminderForm.description}
                onChange={(e) => setReminderForm({ ...reminderForm, description: e.target.value })}
                placeholder="Enter description (optional)"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="reminder-date">Due Date *</Label>
                <DatePicker
                  id="reminder-date"
                  value={reminderForm.date}
                  onChange={(value) => setReminderForm({ ...reminderForm, date: value })}
                  placeholder="MM/DD/YYYY"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reminder-time">Time *</Label>
                <Input
                  id="reminder-time"
                  type="time"
                  value={reminderForm.time}
                  onChange={(e) => setReminderForm({ ...reminderForm, time: e.target.value })}
                  className="w-full"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reminder-priority">Priority</Label>
              <Select 
                value={reminderForm.priority} 
                onValueChange={(value) => setReminderForm({ ...reminderForm, priority: value })}
              >
                <SelectTrigger id="reminder-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Email Notification Section */}
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="send-email"
                  checked={reminderForm.sendEmail}
                  onCheckedChange={(checked) => setReminderForm({ ...reminderForm, sendEmail: checked })}
                />
                <Label htmlFor="send-email" className="text-sm font-medium cursor-pointer">
                  Send email notification
                </Label>
              </div>

              {reminderForm.sendEmail && (
                <div className="space-y-2 pl-6">
                  <Label htmlFor="email-recipient" className="text-sm">Recipient Email</Label>
                  <Input
                    id="email-recipient"
                    type="email"
                    value={reminderForm.emailRecipient}
                    onChange={(e) => setReminderForm({ ...reminderForm, emailRecipient: e.target.value })}
                    placeholder="email@example.com"
                  />
                  <p className="text-xs text-muted-foreground">
                    A notification email will be sent when the reminder is due
                  </p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReminderDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveReminder} 
              disabled={!reminderForm.title || !reminderForm.date || !reminderForm.time}
            >
              Set Reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enhanced Task Dialog */}
      <EnhancedTaskDialog
        open={enhancedDialogOpen}
        onOpenChange={setEnhancedDialogOpen}
        onSave={handleSaveEnhancedTask}
        initialData={selectedTask}
        mode={selectedTask ? 'edit' : 'create'}
      />
    </div>
  );
};

export default TasksCalendarView;
